/**
 * W-9 Form Hook
 * Custom hook for managing W-9 form state using model fields as single source of truth
 */

import { useState, useEffect, useCallback } from "react";
import { W9FormModel } from "../Models/W9Models";
import { W9Service } from "../services/W9Service";

export interface UseW9FormOptions {
  initialData?: any;
}

export interface UseW9FormReturn {
  model: W9FormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (field: string, value: string | boolean) => void;
  updateMultipleFields: (fields: Record<string, string | boolean>) => void;
  validate: () => boolean;
}

export const useW9Form = (options: UseW9FormOptions = {}): UseW9FormReturn => {
  const { initialData } = options;

  // State management using model as single source of truth
  const [model, setModel] = useState<W9FormModel>(() => {
    if (initialData) {
      return W9Service.fromApiResponse(initialData);
    }
    return new W9FormModel();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  // Update validation state when model changes
  useEffect(() => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
  }, [model]);

  /**
   * Update a single field in the model
   */
  const updateField = useCallback((field: string, value: string | boolean) => {
    setModel((prevModel) => {
      const newModel = new W9FormModel(prevModel);
      (newModel as any)[field] = value;
      return newModel;
    });
  }, []);

  /**
   * Update multiple fields in the model
   */
  const updateMultipleFields = useCallback((fields: Record<string, string | boolean>) => {
    setModel((prevModel) => {
      const newModel = new W9FormModel(prevModel);
      Object.entries(fields).forEach(([field, value]) => {
        (newModel as any)[field] = value;
      });
      return newModel;
    });
  }, []);

  /**
   * Validate the current model
   */
  const validate = useCallback((): boolean => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  }, [model]);

  return {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
